import { SiteDef, MaterialType } from '../types';

export const SITES: SiteDef[] = [
  {
    id: 'dusty_creek',
    name: 'Dusty Creek Ranch',
    location: 'Arizona, USA',
    tier: 1,
    gridSize: 12,
    maxLayers: 3,
    seasonDays: 90,
    permitCost: 500,
    travelCost: 0,
    repRequired: 0,
    description: 'A sun-baked ranch on the edge of the Sonoran Desert where arrowheads and pottery shards litter the dry creek bed. Local ranchers have been finding fragments for decades, but nobody has conducted a proper excavation.',
    layerTypes: ['topsoil', 'clay', 'sediment'] as MaterialType[],
    ambientSound: 'desert_wind',
    mapX: 18,
    mapY: 42,
  },
  {
    id: 'millbrook_farm',
    name: 'Millbrook Farm',
    location: 'Somerset, England',
    tier: 1,
    gridSize: 12,
    maxLayers: 4,
    seasonDays: 90,
    permitCost: 800,
    travelCost: 200,
    repRequired: 5,
    description: 'Rolling green hills hide the remains of a Roman-era settlement beneath centuries of English farmland. A farmer plowing his field unearthed a bronze brooch, hinting at much more below.',
    layerTypes: ['topsoil', 'clay', 'sediment', 'rock'] as MaterialType[],
    ambientSound: 'english_countryside',
    mapX: 48,
    mapY: 28,
  },
  {
    id: 'sunken_temple',
    name: 'The Sunken Temple',
    location: 'Yucatan, Mexico',
    tier: 2,
    gridSize: 14,
    maxLayers: 5,
    seasonDays: 75,
    permitCost: 2000,
    travelCost: 500,
    repRequired: 15,
    description: 'A Mayan temple half-swallowed by the jungle and sinking into a cenote. The rising water table threatens to reclaim the site entirely. Work fast before the rains come and the lower chambers flood.',
    layerTypes: ['topsoil', 'clay', 'sediment', 'water', 'rock'] as MaterialType[],
    specialMechanic: 'water_table',
    ambientSound: 'jungle_water',
    mapX: 20,
    mapY: 48,
  },
  {
    id: 'silk_road',
    name: 'Silk Road Waystation',
    location: 'Bukhara, Uzbekistan',
    tier: 2,
    gridSize: 14,
    maxLayers: 4,
    seasonDays: 80,
    permitCost: 1500,
    travelCost: 800,
    repRequired: 20,
    description: 'The ruins of a caravanserai along the ancient Silk Road, buried under centuries of windblown sand. Traders from China, Persia, and Rome all passed through here, leaving behind a tangled mix of cultures.',
    layerTypes: ['sand', 'clay', 'sediment', 'rock'] as MaterialType[],
    ambientSound: 'desert_market',
    mapX: 62,
    mapY: 35,
  },
  {
    id: 'frozen_cairn',
    name: 'The Frozen Cairn',
    location: 'Lofoten, Norway',
    tier: 3,
    gridSize: 14,
    maxLayers: 5,
    seasonDays: 70,
    permitCost: 3000,
    travelCost: 1000,
    repRequired: 30,
    description: 'A Viking burial cairn locked in permafrost high above a fjord. The frozen ground preserves organic materials beautifully but makes excavation brutally slow. Short summer days limit your working season.',
    layerTypes: ['topsoil', 'permafrost', 'clay', 'sediment', 'rock'] as MaterialType[],
    specialMechanic: 'permafrost',
    ambientSound: 'arctic_wind',
    mapX: 50,
    mapY: 22,
  },
  {
    id: 'pharaohs_shadow',
    name: "Pharaoh's Shadow",
    location: 'Valley of the Kings, Egypt',
    tier: 3,
    gridSize: 16,
    maxLayers: 6,
    seasonDays: 90,
    permitCost: 5000,
    travelCost: 1200,
    repRequired: 40,
    description: 'A previously undiscovered tomb entrance in the shadow of a known pharaoh\'s pyramid. The Egyptian authorities are watching closely, and every artifact must be meticulously documented. Six deep layers promise extraordinary finds.',
    layerTypes: ['sand', 'clay', 'sediment', 'rock', 'bedrock', 'sand'] as MaterialType[],
    ambientSound: 'tomb_echo',
    mapX: 55,
    mapY: 42,
  },
  {
    id: 'obsidian_forge',
    name: 'The Obsidian Forge',
    location: 'Hokkaido, Japan',
    tier: 4,
    gridSize: 16,
    maxLayers: 5,
    seasonDays: 80,
    permitCost: 6000,
    travelCost: 1500,
    repRequired: 55,
    description: 'An ancient Jomon-era workshop buried under volcanic ash from a long-dormant caldera. The volcanic layer is incredibly hard to penetrate but has preserved artifacts in remarkable condition beneath it.',
    layerTypes: ['topsoil', 'volcanic', 'clay', 'sediment', 'rock'] as MaterialType[],
    ambientSound: 'volcanic_rumble',
    mapX: 82,
    mapY: 38,
  },
  {
    id: 'atlantean_shelf',
    name: 'Atlantean Shelf',
    location: 'Eastern Mediterranean',
    tier: 4,
    gridSize: 16,
    maxLayers: 6,
    seasonDays: 75,
    permitCost: 8000,
    travelCost: 2000,
    repRequired: 60,
    description: 'A submerged continental shelf where sonar has detected geometric structures beneath the seabed. Underwater excavation is expensive and slow, but the potential for a civilization-rewriting discovery draws you in.',
    layerTypes: ['water', 'sand', 'clay', 'sediment', 'rock', 'bedrock'] as MaterialType[],
    specialMechanic: 'underwater',
    ambientSound: 'deep_ocean',
    mapX: 52,
    mapY: 38,
  },
  {
    id: 'library_of_sands',
    name: 'The Library of the Sands',
    location: 'Central Sahara',
    tier: 5,
    gridSize: 16,
    maxLayers: 6,
    seasonDays: 100,
    permitCost: 10000,
    travelCost: 2500,
    repRequired: 75,
    description: 'Satellite imagery revealed buried structures deep beneath the Saharan dunes — possibly a lost repository of ancient knowledge. Two thick layers of sand guard whatever lies beneath. The heat is merciless and supply runs are costly.',
    layerTypes: ['sand', 'sand', 'clay', 'sediment', 'rock', 'bedrock'] as MaterialType[],
    ambientSound: 'sahara_silence',
    mapX: 48,
    mapY: 42,
  },
  {
    id: 'genesis_cavern',
    name: 'The Genesis Cavern',
    location: 'Afar Triangle, Ethiopia',
    tier: 5,
    gridSize: 16,
    maxLayers: 6,
    seasonDays: 120,
    permitCost: 15000,
    travelCost: 3000,
    repRequired: 85,
    description: 'A vast cave system in the cradle of humanity where the oldest hominid remains have been found. The geology is treacherous — rock and bedrock layers sandwich delicate sediment. A career-defining dig for those with the skill and reputation to attempt it.',
    layerTypes: ['topsoil', 'rock', 'clay', 'sediment', 'bedrock', 'rock'] as MaterialType[],
    ambientSound: 'cavern_drip',
    mapX: 58,
    mapY: 50,
  },
];

export function getSiteDef(id: string): SiteDef | undefined {
  return SITES.find((site) => site.id === id);
}
